// src/services/teamPhotoServices.js
import axios from "axios";

const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const apiUrl = `${baseUrl}/api/team`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace la photo d'un membre Team.
 * - Côté serveur: sharp -> MASTER + THUMB 400x400 (cover), auto-rotate EXIF, format par défaut WEBP.
 * - Retour: { id, photoURL } où photoURL est un chemin public (ex: /uploads/photos_team/...).
 *
 * @param {number|string} teamId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<{id:number, photoURL:string}>}
 */
export const uploadTeamPhoto = async (teamId, file, options = {}) => {
  if (!teamId) throw new Error("teamId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(teamId)}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // Laisse axios gérer le Content-Type (multipart/form-data + boundary)
        },
        onUploadProgress,
      }
    );
    return data; // { id, photoURL }
  } catch (error) {
    console.error("[uploadTeamPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome une photo Team existante côté serveur.
 * - Si 'src' est omis, le serveur utilise Team.photoURL actuel.
 * - x,y,width,height en pixels relatifs au MASTER.
 * - Retour: { id, photoURL } (nouvelle vignette 400x400).
 *
 * @param {number|string} teamId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format]
 * @param {string} [crop.src] - chemin public /uploads/... si tu veux recadrer une autre image
 * @returns {Promise<{id:number, photoURL:string}>}
 */
export const cropTeamPhoto = async (teamId, crop = {}) => {
  if (!teamId) throw new Error("teamId requis");

  const { x, y, width, height, format, src } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
  }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(teamId)}/photo/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );
    return data; // { id, photoURL }
  } catch (error) {
    console.error("[cropTeamPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si photoURL commence par /uploads/ => préfixe avec baseUrl
 * - Sinon renvoie tel quel (utile pour anciennes URLs absolues)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
